import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import {
  BehaviorSubject,
  firstValueFrom,
  map,
  Observable,
  switchMap
} from 'rxjs';
import { OverviewTotals } from '../interfaces/overview-totals.interface';
import { IBusinessCaseGeneration } from '../models/business-case-generation';
import { BusinessCaseSetup } from '../interfaces/business-case-setup.interface';
import { UpdateBusinessCasePayload } from '../interfaces/update-business-case-payload.interface';
import { IBusinessCaseTableFilter } from '../interfaces/business-case-table-filter.interface';
import { IBusinessCaseDetails } from '../interfaces/business-case-details.interface';
import { IBusinessCaseAccount } from '../interfaces/collaborator.interface';
import { IBusinessCaseOverview } from '../interfaces/business-case-overview.interface';
import { IActivityLog } from '../interfaces/activity-log.interface';
import { IBusinessCaseStageConfig } from '../interfaces/business-case-stage-config.interface';
import { format, subDays } from 'date-fns';
import { BusinessCaseReviewPayload } from '../interfaces/business-case-review-payload.interface';
import { IBusinessCaseSoftServiceCostOverview } from '../models/business-case-service-cost-overview.interface';
import { IBusinessCaseSoftServiceQuote } from '../models/business-case-soft-service-quote';
import { SiteRetrieval } from '../interfaces/site-retrieval.interface';
import { UserBusinessCase } from '../interfaces/user-business-case.interface';
import { UpdateUserBusinessCasesPayload } from '../interfaces/update-user-business-cases-payload.interface';
import { ParamMap } from '@angular/router';
import { IBusinessCaseEngineeringServiceQuote } from '../models/business-case-engineering-service-quote';
import { UpdateChangeNotePayload } from '../interfaces/update-change-note-payload.interface';
import { IRelocationComparison } from '../models/relocation-site-comparison';
import { IExecutionActivityViewModel } from '../interfaces/execution-activity.interface';
import { ICommercialSignOffPayload } from '../interfaces/commercial-sign-off-payload.interface';

@Injectable({
  providedIn: 'root'
})
export class BusinessCaseService {
  public businessCaseSetup!: BusinessCaseSetup;
  public defaultRangeDaysCount = 7;
  public defaultFilter: IBusinessCaseTableFilter = {
    currentPage: 1,
    size: 10000,
    sortBy: 'initiationDate',
    sortDirection: 'Desc',
    startDate: format(
      subDays(new Date(), this.defaultRangeDaysCount),
      'yyyy-MM-dd'
    ), // yyyy-MM-dd or null to ignore
    endDate: format(new Date(), 'yyyy-MM-dd'), // yyyy-MM-dd or null to ignore
    searchTerm: '' // searches business case name
  };
  public businessCaseTableFilter: BehaviorSubject<IBusinessCaseTableFilter> =
    new BehaviorSubject(this.defaultFilter);

  public businessCaseTableFilter$: Observable<IBusinessCaseTableFilter> =
    this.businessCaseTableFilter.asObservable();

  private baseUrl: string = environment.apiUrl;
  private readonly _http: HttpClient = inject(HttpClient);

  public async initializeBusinessCase(businessCaseType: any): Promise<void> {
    await firstValueFrom(
      this._http.post<BusinessCaseSetup>(`${this.baseUrl}/business-cases`,
        businessCaseType
      )
    ).then((res) => {
      this.businessCaseSetup = res;
    });
  }

  public generateBusinessCaseTable(): Observable<IBusinessCaseGeneration> {
    return this.businessCaseTableFilter$.pipe(
      switchMap((filterData) =>
        this._http.put<IBusinessCaseGeneration>(
          `${this.baseUrl}/business-cases/table`,
          filterData
        )
      )
    );
  }

  public fetchBusinessCaseById(id: number): Observable<IBusinessCaseDetails> {
    return this._http.get<IBusinessCaseDetails>(
      `${this.baseUrl}/business-cases/${id}`
    );
  }

  public fetchDoc(
    businessCaseId: number,
    drawingId: number,
    drawingUrl: string
  ): Observable<Blob> {
    return this._http.get(
      `${this.baseUrl}/business-cases/${businessCaseId}/documents/${drawingId}/${drawingUrl}`,
      {
        responseType: 'blob'
      }
    );
  }

  public fetchOverviewTotals(): Observable<OverviewTotals> {
    return this._http.get<OverviewTotals>(
      `${this.baseUrl}/business-cases/overview`
    )
  }

  public updateBusinessCase(
    payload: UpdateBusinessCasePayload
  ): Observable<void> {
    return this._http.put<void>(`${this.baseUrl}/business-cases`, payload);
  }

  public updateChangeNote(
    payload: UpdateChangeNotePayload
  ): Observable<void> {
    return this._http.put<void>(`${this.baseUrl}/business-cases`, payload);
  }

  public fetchBusinessCaseAccounts(): Observable<Array<IBusinessCaseAccount>> {
    return this._http.get<Array<IBusinessCaseAccount>>(
      `${this.baseUrl}/business-cases/users`
    );
  }

  public fetchBusinessCaseAccountsByProvince(
    province: string
  ): Promise<Array<IBusinessCaseAccount>> {
    return firstValueFrom(
      this._http.get<Array<IBusinessCaseAccount>>(
        `${this.baseUrl}/business-cases/users/` + province
      )
    );
  }

  public fetchBusinessCaseOverview(
    id: number
  ): Observable<IBusinessCaseOverview> {
    return this._http.get<IBusinessCaseOverview>(
      `${this.baseUrl}/business-cases/${id}/overview`
    );
  }

  public fetchActivityLogs(id: number): Observable<Array<IActivityLog>> {
    return this._http.get<Array<IActivityLog>>(
      `${this.baseUrl}/business-cases/${id}/activities`
    );
  }

  public addNewComment(payload: {
    id: number;
    comment: string;
  }): Observable<void> {
    return this._http.put<void>(
      `${this.baseUrl}/business-cases/comments`,
      payload
    );
  }

  public fetchBusinessCaseSoftService(
    businessCaseId: number
  ): Promise<Array<IBusinessCaseSoftServiceCostOverview>> {
    return firstValueFrom(
      this._http.get<Array<IBusinessCaseSoftServiceCostOverview>>(
        `${this.baseUrl}/business-cases/${businessCaseId}/overview/soft-services`
      )
    );
  }

  public fetchBusinessCaseStagesConfigs(
    businessCaseId: number
  ): Observable<Array<IBusinessCaseStageConfig>> {
    return this._http.get<Array<IBusinessCaseStageConfig>>(
      `${this.baseUrl}/business-cases/${businessCaseId}/stages`
    );
  }

  public submitBusinessCaseForReview(payload: {
    businessCaseId: number;
    businessCaseStageId: number;
  }): Observable<void> {
    return this._http.put<void>(
      `${this.baseUrl}/business-cases/status/review`,
      payload
    );
  }

  public deleteBusinessCase(businessCaseId: number): Observable<void> {
    return this._http.delete<void>(
      `${this.baseUrl}/business-cases/${businessCaseId}`
    );
  }

  public rejectBusinessCase(
    payload: BusinessCaseReviewPayload
  ): Observable<void> {
    return this._http.put<void>(
      `${this.baseUrl}/business-cases/review`,
      payload
    );
  }

  public approveBusinessCase(
    payload: BusinessCaseReviewPayload
  ): Observable<void> {
    return this._http.put<void>(
      `${this.baseUrl}/business-cases/review`,
      payload
    );
  }

  public fetchBusinessCaseSoftServiceQuote(
    businessCaseId: number
  ): Observable<IBusinessCaseSoftServiceQuote> {
    return this._http.get<IBusinessCaseSoftServiceQuote>(
      `${this.baseUrl}/business-cases/${businessCaseId}/soft-services/quote`
    );
  }

  public fetchBusinessCaseHardServiceQuote(
    businessCaseId: number
  ): Observable<IBusinessCaseEngineeringServiceQuote> {
    return this._http.get<IBusinessCaseEngineeringServiceQuote>(
      `${this.baseUrl}/business-cases/${businessCaseId}/engineering-services/quote`
    );
  }

  public resolveBCDelay(payload: FormData): Observable<any> {
    return this._http.put(
      `${this.baseUrl}/business-cases/delayed/reason`,
      payload
    );
  }

  public downloadBCDocument(docUrl: string): Observable<any> {
    return this._http.get(`${this.baseUrl}/${docUrl}`, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  public deleteBCDocument(
    businessCaseId: number,
    documentId: number
  ): Observable<void> {
    return this._http.delete<void>(
      `${this.baseUrl}/business-cases/${businessCaseId}/documents/${documentId}`
    );
  }

  public fetchBuildingList(): Observable<Array<SiteRetrieval>> {
    return this._http
      .get<Array<SiteRetrieval>>(
        `${this.baseUrl}/business-cases/buildings`
      )
      .pipe(map((res) => res));
  }
  public fetchBuilding(siteId: string): Observable<SiteRetrieval> {
    return this._http
      .get<SiteRetrieval>(
        `${this.baseUrl}/business-cases/${siteId}/sites`
      )
      .pipe(map((res) => res));
  }

  public searchBuildings(buildingNumbers: Array<string>): Observable<Array<SiteRetrieval>> {
    return this._http.post<Array<SiteRetrieval>>(
      `${this.baseUrl}/business-cases/buildings/search`,
      buildingNumbers
    );
  }

  public getRelocationComparison(businessCaseId: number): Observable<IRelocationComparison> {
    return this._http.get<IRelocationComparison>(
      `${this.baseUrl}/business-cases/${businessCaseId}/relocation-comparison`
    );
  }

  public fetchUserBusinessCases(
    userId: number
  ): Promise<Array<UserBusinessCase>> {
    return firstValueFrom(
      this._http.get<Array<UserBusinessCase>>(
        `${this.baseUrl}/accounts/${userId}/business-cases`
      )
    );
  }

  public updateUserBusinessCases(
    userId: number,
    payload: Array<UpdateUserBusinessCasesPayload>
  ): Promise<Array<UserBusinessCase>> {
    return firstValueFrom(
      this._http.put<Array<UserBusinessCase>>(
        `${this.baseUrl}/accounts/${userId}/business-cases`,
        payload
      )
    );
  }

  public generateFullChangeNotePdf(businessCaseId: number): Observable<void> {
    return this._http.put<void>(`${this.baseUrl}/business-cases/${businessCaseId}/full-change-notes`, null);
  }

  public uploadSignedChangeNote(businessCaseId: number, file: File): Observable<string> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    return this._http.post<string>(
      `${this.baseUrl}/business-cases/${businessCaseId}/change-note/upload-signed`,
      formData
    );
  }

  public undoExecutionActivity(businessCaseId: number, activityId: number): Observable<void> {
    return this._http.put<void>(
      `${this.baseUrl}/business-cases/${businessCaseId}/execution-activities/${activityId}/undo`,
      {}
    );
  }

  public fetchExecutionActivities(businessCaseId: number): Observable<Array<IExecutionActivityViewModel>> {
    return this._http.get<Array<IExecutionActivityViewModel>>(
      `${this.baseUrl}/business-cases/${businessCaseId}/execution-activities`
    );
  }

  public acknowledgeProcurementActivity(businessCaseId: number, activityId: number, notes?: string): Observable<void> {
    return this._http.put<void>(
      `${this.baseUrl}/business-cases/${businessCaseId}/execution-activities/${activityId}/acknowledge-procurement`,
      { notes }
    );
  }

  public acknowledgeEngineeringActivity(
    businessCaseId: number,
    activityId: number,
    evidence?: File,
    notes?: string
  ): Observable<void> {
    const formData: FormData = new FormData();
    if (evidence) {
      formData.append('evidence', evidence);
    }
    if (notes) {
      formData.append('notes', notes);
    }
    return this._http.put<void>(
      `${this.baseUrl}/business-cases/${businessCaseId}/execution-activities/${activityId}/acknowledge-engineering`,
      formData
    );
  }

  public commercialSignOff(payload: ICommercialSignOffPayload): Observable<void> {
    return this._http.put<void>(
      `${this.baseUrl}/business-cases/commercial-sign-off`,
      payload
    );
  }

  public isReadOnlyView(routeParams: ParamMap): boolean {
    const isReadOnlyView =
      routeParams.get('read-only') === undefined ||
      routeParams.get('read-only') === null
        ? false
        : true;

    return isReadOnlyView;
  }

  public isEditor(
    bcStagesConfigs: Array<IBusinessCaseStageConfig>,
    bcDetails: IBusinessCaseDetails, //TODO remove
    currentStageName = ''
  ): boolean {
    return bcStagesConfigs.find(
      (config) => config.stageName === currentStageName
    )?.canEdit as boolean;
  }

  // BP-118: canRead also covers the stage owner and Governance/Admin, but the backend only
  // accepts a review from a listed reviewer - canReview is that check. False until the stage
  // config has loaded, so review actions stay hidden while permissions are unresolved
  public isReviewer(
    bcStagesConfigs: Array<IBusinessCaseStageConfig>,
    bcDetails: IBusinessCaseDetails, //TODO remove
    currentStageName = ''
  ): boolean {
    return bcStagesConfigs.find(
      (config) => config.stageName === currentStageName
    )?.canReview === true;
  }

  public includesSoftServices(businessCase: IBusinessCaseDetails): boolean {
    return (
      businessCase?.businessCaseType! === 'ENGINEERING_AND_SOFT_SERVICES' ||
      businessCase?.businessCaseType! === 'SOFT_SERVICES' ||
      businessCase?.businessCaseType! === 'CATERING' ||
      businessCase?.businessCaseType! === 'ATM_SINGLE_BC' ||
      businessCase?.businessCaseType! === 'ATM_GROUP_BC' ||
      businessCase?.businessCaseType! === 'OCCUPATIONAL_HEALTH_AND_SAFETY'
    );
  }

  public includesEngineeringServices(
    businessCase: IBusinessCaseDetails
  ): boolean {
    return (
      businessCase?.businessCaseType! === 'ENGINEERING_SERVICES' ||
      businessCase?.businessCaseType! === 'ENGINEERING_AND_SOFT_SERVICES'
    );
  }
}
